import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { SiteStatus } from '~/handlers/ports/api-fetch-site-status.port';
import { fetchSiteStatusHandler } from '~/handlers/fetch-site-status.handler';

// Fallback de código: o site desligado, com tudo pausado por baixo. Vale só
// enquanto o core nunca respondeu neste navegador — se ele cair, o site não
// reabre sozinho.
const PAUSED_FALLBACK: SiteStatus = {
  homeNoticeEnabled: true,
  gamesEnabled: false,
  depositsEnabled: false,
  registrationsEnabled: false,
  siteShutdown: true,
  siteShutdownIn: null,
  notices: {},
};

// Revalida com o site aberto, pra que religar algo no core chegue sem F5.
const POLL_INTERVAL_MS = 60 * 1000;

// Acima disso o setTimeout dispara na hora (estouro de 32 bits); um agendamento
// tão distante é reagendado pelos refreshes seguintes, mais perto da hora.
const MAX_TIMEOUT_MS = 2 ** 31 - 1;

// Folga depois do início: o boot seguinte (a virada recarrega a página) tem que
// achar o core já desligado, e a parte aleatória espalha os jogadores pra não
// chegarem todos no mesmo segundo.
const SHUTDOWN_MARGIN_S = 2;
const SHUTDOWN_SPREAD_S = 8;

// Operações do site (ENG-915): o que o core liga e desliga em Administrativo ›
// Operações do site, lido de GET /api/site-status. Persistido: a última resposta
// boa é o fallback da próxima visita quando o core não responder.
export const useSiteStatusStore = defineStore(
  'ap.site-status',
  () => {
    const status = ref<SiteStatus>({ ...PAUSED_FALLBACK });

    let pollingStarted = false;
    let shutdownTimer: ReturnType<typeof setTimeout> | undefined;
    let shutdownTimerFrom: SiteStatus | undefined;

    // Desligamento agendado no core: vira na hora, sem esperar o polling. Cada
    // resposta nova reprograma — um agendamento movido ou cancelado chega junto.
    function scheduleShutdown(from: SiteStatus) {
      // O handler devolve o MESMO objeto enquanto a resposta está em cache
      // (30 s): o timer programado a partir dela continua certo, e reprogramar
      // com o mesmo `siteShutdownIn` relativo empurraria a virada pra depois.
      if (from === shutdownTimerFrom) return;

      shutdownTimerFrom = from;
      clearTimeout(shutdownTimer);

      const secondsToStart = from.siteShutdownIn;

      if (secondsToStart === null || !import.meta.client) return;

      const delay =
        (secondsToStart +
          SHUTDOWN_MARGIN_S +
          Math.random() * SHUTDOWN_SPREAD_S) *
        1000;

      if (delay > MAX_TIMEOUT_MS) return;

      shutdownTimer = setTimeout(() => {
        status.value = {
          ...status.value,
          siteShutdown: true,
          siteShutdownIn: null,
        };
      }, delay);
    }

    async function refresh() {
      const fresh = await fetchSiteStatusHandler();

      if (fresh) {
        status.value = fresh;
        scheduleShutdown(fresh);
      }
    }

    function startPolling() {
      if (pollingStarted || !import.meta.client) return;

      pollingStarted = true;

      setInterval(() => {
        if (document.visibilityState === 'visible') refresh();
      }, POLL_INTERVAL_MS);

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') refresh();
      });
    }

    return {
      status,
      refresh,
      startPolling,
    };
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.localStorage(),
      pick: ['status'],
    },
  },
);
